// Components==============
import BuilderCard from 'components/UI/BuilderCard/BuilderCard';
import Dropdown from 'components/UI/Dropdown/Dropdown';
import FormDropdown from 'components/UI/Form/FormDropdown';
import Input from 'components/UI/Form/Input';
import Textarea from 'components/UI/Form/Textarea';
import ImagesUpload from 'components/UI/ImagesUpload/ImagesUpload';
import { motion } from 'framer-motion';
import { useRedux } from 'hooks/useRedux';
import { useTranslate } from 'hooks/useTranslate';
import React, { useContext, useEffect, useState } from 'react';
import FormContext from 'utilities/FormContext';
import { TrainingBuilderContext } from '../../TrainingBuilder';
import ExtraInfo from './ExtraInfo/ExtraInfo';
import './TrainingEditor.module.scss';
// =========================

export default function TrainingEditor() {
  const {
    images,
    training,
    formValues,
    handleChange,
    errors,
    setFormValues,
    handleAttachmentUpload,
    handleRemoveAttachment,
    trainingId,
    loading,
    editorView,
  } = useContext(TrainingBuilderContext);

  const translate = useTranslate();

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);

  const categoryItems = useRedux((state) =>
    state.category?.entities.allIds.map(
      (id) => state.category?.entities.byId[id]
    )
  );

  const skillItems = useRedux((state) =>
    state.skill?.entities.allIds.map((id) => state.skill?.entities.byId[id])
  );

  useEffect(() => {
    const title = training?.title || '';
    const categories =
      training?.categories?.map((cat) => cat.category_id) || [];
    const skills = training?.skills?.map((cat) => cat.skill_id) || [];
    const description = training?.description || '';
    const youtube = training?.youtube || '';

    if (training) {
      setSelectedCategories(
        training?.categories?.map((c) => c.category.name) || []
      );
      setSelectedSkills(training?.skills?.map((s) => s.skill.name) || []);

      setFormValues((prev) => ({
        ...prev,
        title,
        categories,
        skills,
        description,
        youtube,
      }));
    }
  }, []);

  useEffect(() => {
    if (typeof selectedSkills !== 'undefined')
      setFormValues((prev) => ({
        ...prev,
        categories: selectedCategories.map(
          (item) => categoryItems?.find((cat) => cat?.name === item)?.id || 0
        ),
      }));
  }, [selectedCategories?.length]);

  useEffect(() => {
    if (typeof selectedSkills !== 'undefined')
      setFormValues((prev) => ({
        ...prev,
        skills: selectedSkills.map(
          (item) => skillItems?.find((skill) => skill?.name === item)?.id || 0
        ),
      }));
  }, [selectedSkills?.length]);

  return (
    <motion.div
      exit={{ opacity: 0, display: 'none' }}
      initial={{ opacity: 0, display: 'none' }}
      animate={
        editorView === 'training'
          ? { opacity: 1, display: 'block' }
          : { opacity: 0, display: 'none' }
      }
    >
      <BuilderCard
        title={
          trainingId
            ? translate('course.terms.course-information')
            : translate('course.actions.create-course')
        }
        loading={loading}
      >
        <FormContext
          onChange={handleChange}
          errors={errors}
          formValues={formValues}
          setFormValues={setFormValues}
        >
          <div styleName="wrapper">
            <div>
              <h2>
                {translate('general.terms.title')} <span>*</span>
              </h2>
              <p styleName="text">
                {translate('course.descriptions.description01')}
              </p>
              <Input
                color="grey"
                name="title"
                placeholder={translate('general.terms.title')}
              />
            </div>
            <div>
              <h2>
                {translate('general.terms.description')} <span>*</span>
              </h2>
              <p styleName="text">
                {translate('course.descriptions.description02')}
              </p>
              <Textarea
                color="grey"
                name="description"
                placeholder={translate('general.terms.description')}
              />
            </div>
            <div>
              <h2>{translate('general.terms.imagery')}</h2>
              <p styleName="text">
                {translate('course.descriptions.description03')}
              </p>
              <div styleName="upload-wrapper">
                <ImagesUpload
                  images={images}
                  handleUpload={handleAttachmentUpload}
                  handleDelete={(c) => handleRemoveAttachment(c.evidenceId)}
                />
              </div>
              <Input
                color="grey"
                name="youtube"
                placeholder="Youtube link"
                required={false}
              />
            </div>
            <div>
              <h2>
                {translate('general.terms.categories')} <span>*</span>
              </h2>
              <p styleName="text">
                {translate('course.descriptions.description04')}
              </p>
              <FormDropdown
                name="categories"
                dropdown={
                  <Dropdown
                    selectedItems={selectedCategories}
                    setSelectedItems={setSelectedCategories}
                    initialOptions={
                      categoryItems?.map((cat) => cat?.name || '') || []
                    }
                    initialText={translate('filters.category-filter.select')}
                    color="grey"
                  />
                }
              />
            </div>
            <div>
              <h2>
                {translate('course.connected-skills.title')} <span>*</span>
              </h2>
              <p styleName="text">
                {translate('course.descriptions.description05')}
              </p>

              <FormDropdown
                name="skills"
                dropdown={
                  <Dropdown
                    selectedItems={selectedSkills}
                    setSelectedItems={setSelectedSkills}
                    initialOptions={
                      skillItems?.map((skill) => skill?.name || '') || []
                    }
                    initialText={translate('filters.skill-filter.select')}
                    color="grey"
                  />
                }
              />
            </div>
          </div>
        </FormContext>
        <div styleName="bar" />
        <ExtraInfo />
      </BuilderCard>
    </motion.div>
  );
}
